import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmacaoProvider } from './context/ConfirmacaoContext';
import { AcessosRHProvider } from './context/AcessosRHContext';
import CarimboBuild from './components/CarimboBuild.jsx';
import { instalaCarcaca } from './utils/carcacaGestos';
instalaCarcaca();   // (V1919) a folha que acompanha o dedo e o pé que sobe com o teclado — uma vez, para todas as salas
import SalaSobDemanda from './components/SalaSobDemanda.jsx';
import { SALAS, sobDemanda, preBaixar, CARREGAR } from './data/salasSobDemanda';

import SeletorPortal from './pages/SeletorPortal/SeletorPortal.jsx';
import Login   from './pages/Login/Login';
import TrocaObrigatoria from './pages/Login/TrocaObrigatoria.jsx';
import Dashboard from './pages/Dashboard/Dashboard';

import './index.css';
import './shared.css';
/* ⚠ `Sala1.css` REMOVIDO (28/08/2026). Tinha 13 linhas: quatro
   cabeçalhos de seção vazios e duas regras `.condicao-tag` do checklist
   antigo, sem JSX nenhum. Ficou invisível porque os portões de CSS liam
   só o `index.css` — corrigidos hoje, o arquivo apareceu vazio.

   A Sala 1 passou a sessão inteira declarada "em zero", e estava: o que
   faltava era o portão enxergar os outros seis arquivos de CSS. */

import './pages/Admin/Admin.css';

/* (V1849) AS SALAS SOB DEMANDA: cada sala (e as telas que só alguns usam: o administrativo, a Administração
   Geral, o portal do RH, a votação) é um pedaço baixado quando se entra nela — e as salas com acesso são
   pré-baixadas logo depois do login. Ver data/salasSobDemanda.js (o porquê e o cuidado com o offline). */
const Admin = sobDemanda(() => import('./pages/Admin/Admin'));
const AdministracaoGeral = sobDemanda(() => import('./pages/AdministracaoGeral/AdministracaoGeral.jsx'));
const DashboardRH = sobDemanda(() => import('./pages/PortalRH/DashboardRH.jsx'));
const SalaRHPlaceholder = sobDemanda(() => import('./pages/PortalRH/SalaRHPlaceholder.jsx'));
const Sala9RH = sobDemanda(() => import('./pages/PortalRH/Sala9/Sala9.jsx'));
const Sala10RH = sobDemanda(() => import('./pages/PortalRH/Sala10/Sala10.jsx'));
const GestaoAcessoRH = sobDemanda(() => import('./pages/PortalRH/GestaoAcesso/GestaoAcesso.jsx'));
const VotacaoPublica = sobDemanda(() => import('./pages/VotacaoPublica/VotacaoPublica.jsx'));
const SD = (el) => <SalaSobDemanda>{el}</SalaSobDemanda>;
/* o pré-download das salas que a pessoa tem acesso (a Sala 1 primeiro) — uma vez, quando os acessos chegam */
function PreBaixarSalas() {
  const { acessosProntos, acessos, isMaster } = useAuth();
  React.useEffect(() => {
    if (!acessosProntos) return;
    preBaixar(isMaster ? Object.keys(CARREGAR) : Object.entries(acessos || {}).filter(([k, v]) => !k.includes('›') && v?.nivel && v.nivel !== 'nenhum').map(([k]) => k));
  }, [acessosProntos, isMaster, acessos]);
  return null;
}

const RotaProtegida = ({ children }) => {
  const { usuario, carregando, acessosProntos } = useAuth();
  if (carregando) return <div className="flex-center" style={{ minHeight:'100vh' }}>Carregando...</div>;
  if (!usuario) return <Navigate to="/login" replace />;

  /* TROCA OBRIGATÓRIA DE SENHA (13/08/2026) — a guarda que faltava.
     A flag `primeiro_acesso` existia no banco, o backend a marcava ao
     resetar senha, o login a devolvia e o AuthContext sabia limpá-la.
     Mas NADA olhava pra ela: nenhuma tela, nenhuma guarda. Todo usuário
     criado mantinha indefinidamente a senha escolhida pelo Master, e a
     mensagem "Usuário deverá trocar no próximo acesso" que o backend
     devolve era falsa.

     Vem ANTES de acessosProntos de propósito: quem precisa trocar senha
     não deveria esperar o mapa de permissões carregar — ele não vai usar
     nenhuma sala até trocar. E fica dentro da RotaProtegida porque ela é
     o ponto único por onde toda rota logada passa; pôr em cada rota
     deixaria alguma de fora. */
  if (usuario.primeiroAcesso) return <TrocaObrigatoria />;

  // Segura a tela até o mapa de permissões estar pronto — evita renderizar
  // o Dashboard com o menu de salas vazio logo após o login (antes exigia F5).
  if (!acessosProntos) return <div className="flex-center" style={{ minHeight:'100vh' }}>Carregando...</div>;
  return <>{children}<PreBaixarSalas /></>;
};
const RotaPublica = ({ children }) => {
  const { usuario, carregando, podeAcessarRH, podeAcessarGeral, acessosExtrasProntos } = useAuth();
  if (carregando) return null;
  if (!usuario) return children;
  // Achado real ao testar num navegador de verdade (09/08/2026): sem
  // esperar acessosExtrasProntos, essa decisão corria com
  // podeAcessarRH/Geral ainda no valor inicial (false) — mandava todo
  // Master RH/Geral direto pro SGQ, sem chance de correção depois (o
  // componente já não estaria mais montado quando a resposta real
  // chegasse). Essa espera é rápida (as duas chamadas em geral
  // respondem em bem menos de 1s) e só afeta esse instante do login,
  // não o resto do app.
  if (!acessosExtrasProntos) return null;
  // Só quem tem acesso a mais de um lado vê o seletor — pra todo mundo
  // (a esmagadora maioria hoje), nada muda: cai direto no SGQ, como
  // sempre foi.
  return <Navigate to={(podeAcessarRH || podeAcessarGeral) ? '/portal' : '/dashboard'} replace />;
};

export default function App() {
  return (
    <AuthProvider>
      <ConfirmacaoProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login"     element={<RotaPublica><Login /></RotaPublica>} />
          <Route path="/dashboard" element={<RotaProtegida><Dashboard /></RotaProtegida>} />
          {(() => {
            const salas = Object.fromEntries(Object.entries(SALAS).map(([n, C]) => [n, SD(<C />)]));
            // Gera as rotas a partir das CHAVES do objeto acima — uma
            // sala nova só precisa entrar aqui, nunca mais numa segunda
            // lista separada (25/07/2026: essa era a causa raiz de
            // Sala 38 e 39 não navegarem — existiam no objeto, mas a
            // lista de números que gerava as rotas parava no 37 e
            // nunca foi atualizada junto).
            return Object.keys(salas).map(n => (
              <Route key={n} path={`/sala/${n}`} element={<RotaProtegida><Dashboard sala={salas[n]} /></RotaProtegida>} />
            ));
          })()}
          <Route path="/admin" element={<RotaProtegida><Dashboard sala={SD(<Admin />)} /></RotaProtegida>} />
          <Route path="/portal" element={<RotaProtegida><SeletorPortal /></RotaProtegida>} />
          <Route path="/rh/dashboard" element={<RotaProtegida><AcessosRHProvider>{SD(<DashboardRH />)}</AcessosRHProvider></RotaProtegida>} />
          <Route path="/rh/sala/9" element={<RotaProtegida><AcessosRHProvider>{SD(<DashboardRH sala={SD(<Sala9RH />)} />)}</AcessosRHProvider></RotaProtegida>} />
          <Route path="/rh/sala/10" element={<RotaProtegida><AcessosRHProvider>{SD(<DashboardRH sala={SD(<Sala10RH />)} />)}</AcessosRHProvider></RotaProtegida>} />
          <Route path="/rh/gestao-acesso" element={<RotaProtegida><AcessosRHProvider>{SD(<DashboardRH sala={SD(<GestaoAcessoRH />)} />)}</AcessosRHProvider></RotaProtegida>} />
          <Route path="/administracao-geral" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          <Route path="/geral/dashboard" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          <Route path="/geral/empresas" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          <Route path="/geral/estrutura" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          <Route path="/geral/lixeira" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          <Route path="/geral/log" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          {/* Sala de provas — Fase 3 do plano de reestruturação (30/08/2026) */}
          <Route path="/geral/provas" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          {/* (V1883) o Servidor — tudo o que estava na Governança da Administração do SGQ */}
          <Route path="/geral/servidor" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          <Route path="/geral/privacidade" element={<RotaProtegida>{SD(<AdministracaoGeral />)}</RotaProtegida>} />
          <Route path="/rh/sala/:numero" element={<RotaProtegida><AcessosRHProvider>{SD(<DashboardRH sala={SD(<SalaRHPlaceholder />)} />)}</AcessosRHProvider></RotaProtegida>} />
          <Route path="/votacao/:token" element={SD(<VotacaoPublica />)} />
          {/* Rota pública de inspeção REMOVIDA em 15/08/2026 (decisão do
              Juan): quem usa o sistema tem login e teto de permissão, e o
              fluxo sem conta perdeu o propósito. O QR físico continua,
              agora como atalho autenticado — /sala/2?qr=token. */}
          <Route path="/"      element={<Navigate to="/dashboard" replace />} />
          <Route path="*"      element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      {/* Fora do `BrowserRouter` de propósito: o carimbo não pertence a
          rota nenhuma e não pode sumir ao trocar de sala ou de portal.
          Dentro do `AuthProvider` porque é ele quem sabe se a pessoa é
          master — o próprio componente decide se aparece. */}
      <CarimboBuild />
      </ConfirmacaoProvider>
    </AuthProvider>
  );
}
